import { Capacitor } from '@capacitor/core';

/** The browser needs the VAPID server public key as an array of Unsigned 8-bit integers (UInt8Array),
 * but we have it stored in Base64, this does the conversion */
export const urlB64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
	const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

	const rawData = atob(base64);
	const outputArray = new Uint8Array(rawData.length);

	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
};

const serviceWorkerPath = import.meta.env.DEV
	? '/src/common/notifications/service-worker.js'
	: '/service-worker.js';

export class WebAppServiceWorker {
	private _vapidDetails: PushSubscription | null = null;

	constructor() {}

	/** Register the service worker and return the service worker registration- it is safe to call this as often as
	 * required, if already registered it will just return the current registration */
	public async register(): Promise<ServiceWorkerRegistration | null> {
		if (Capacitor.isNativePlatform()) return null;
		if (!('serviceWorker' in navigator)) return null;

		const serviceWorkerRegistration = await navigator.serviceWorker.register(serviceWorkerPath, {
			type: import.meta.env.DEV ? 'module' : 'classic',
		});
		return serviceWorkerRegistration;
	}

	/** Get the current VAPID Details object for this browser client */
	public async vapidDetails() {
		if (this._vapidDetails) return this._vapidDetails;

		// Check if VAPID public key is configured
		const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
		if (!vapidPublicKey) {
			throw new Error('VITE_VAPID_PUBLIC_KEY environment variable is not configured');
		}

		const serviceWorkerRegistration = await this.register();
		if (!serviceWorkerRegistration) return null;

		if (!import.meta.env.VITE_VAPID_PUBLIC_KEY) {
			throw new Error('VITE_VAPID_PUBLIC_KEY is not set in .env');
		}

		const subscription = await this.subscribe(vapidPublicKey);

		if (subscription) {
			this._vapidDetails = subscription;
		} else {
			// There is a failure mode when the applicationServerKey changes. You need to call unsubscribe to get a new subscription.
			await this.unsubscribeAll();
			const secondAttempt = await this.subscribe(vapidPublicKey);

			this._vapidDetails = secondAttempt;
		}

		return this._vapidDetails;
	}

	private async subscribe(vapidPublicKey: string) {
		const serviceWorkerRegistration = await this.register();
		if (!serviceWorkerRegistration) return null;
		try {
			const subscription = await serviceWorkerRegistration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: urlB64ToUint8Array(vapidPublicKey),
			});
			return subscription;
		} catch (error) {
			console.error('Failed to subscribe to push notifications', error);
			return null;
		}
	}

	/** Unsubscribe all current notification registrations */
	public async unsubscribeAll() {
		const serviceWorkerRegistration = await this.register();
		if (serviceWorkerRegistration) {
			const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
			if (subscription) {
				return await subscription?.unsubscribe();
			}
		}
	}
}

export const getNotificationPermission = async (): Promise<boolean> => {
	if (Notification.permission === 'denied' || Notification.permission === 'default')
		await Notification.requestPermission();

	return Notification.permission === 'granted';
};

export const pushNotificationsWereDenied = (): boolean => Notification.permission === 'denied';
export const pushNotificationsWereGranted = (): boolean => Notification.permission === 'granted';

export const pushNotificationsAreSupported = (): boolean => 'PushManager' in window;
